import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { NotificationSendService } from './services/notification-send.service';
import { FundService } from '@features/funds/services/fund.service';
import { NotificationService } from '@features/notifications/services/notification.service';
import { AlertService } from '@shared/services/alert.service';
import { AlertType } from '@utils/enums/alert-type';
import {
  IJob,
  IParticipant,
  IEstadoCuentaRequest,
  IJobStatus,
} from './models/notification-send';
import { INotification } from '@features/notifications/models/notification';
import { IFund } from '@features/funds/models/fund';

@Component({
  selector: 'app-notifications-send',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './notifications-send.component.html',
  styleUrl: './notifications-send.component.css'
})
export class NotificationsSendComponent implements OnInit, OnDestroy {
  private readonly notificationSendSrv: NotificationSendService = inject(NotificationSendService);
  private readonly fundSrv: FundService = inject(FundService);
  private readonly notificationSrv: NotificationService = inject(NotificationService);
  private readonly alertSrv: AlertService = inject(AlertService);

  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<void>();
  private pollingInterval: ReturnType<typeof setInterval> | null = null;

  public isLoading: boolean = true;
  public showJobsTable: boolean = true;
  public showParticipantsTable: boolean = false;
  public selectedFund: IFund | null = null;

  public jobs: IJob[] = [];
  public paginatedJobs: IJob[] = [];
  public currentJobsPage: number = 1;
  public jobsItemsPerPage: number = 5;
  public jobsTotalPages: number = 1;

  public participants: IParticipant[] = [];
  public filteredParticipants: IParticipant[] = [];
  public paginatedParticipants: IParticipant[] = [];
  public currentPage: number = 1;
  public itemsPerPage: number = 10;
  public totalPages: number = 1;
  public searchTerm: string = '';
  public funds: IFund[] = [];
  public activeNotifications: INotification[] = [];
  public selectedParticipantsSet = new Set<string>();
  public selectedNotification: INotification | null = null;
  public subject: string = '';
  public receiverType: string = 'Email';

  public month: number = new Date().getMonth() + 1;
  public year: number = new Date().getFullYear();
  public activeJobStatus: IJobStatus | null = null;
  public isPolling: boolean = false;

  public isLoadingJobs: boolean = true;
  public isLoadingParticipants: boolean = false;
  public isLoadingFunds: boolean = true;
  public isLoadingNotifications: boolean = true;
  public isSendingNotification: boolean = false;
  public selectedFiles: File[] = [];
  public fileValidationError: string | null = null;

  readonly months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];

  get jobsPages(): number[] {
    return Array.from({ length: this.jobsTotalPages }, (_, i) => i + 1);
  }

  get participantPages(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get selectedParticipants(): string[] {
    return Array.from(this.selectedParticipantsSet);
  }

  get monthName(): string {
    return this.months.find(m => m.value === this.month)?.label ?? '';
  }

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      try {
        this.currentPage = 1;
        this.updatePagination();
      } catch (err) {
        console.error('Error al filtrar participantes', err);
      }
    });

    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling();
  }

  loadInitialData(): void {
    this.isLoading = true;
    this.loadJobs();
    this.loadFunds();
    this.loadActiveNotifications();
  }

  loadJobs(): void {
    this.isLoadingJobs = true;
    this.notificationSendSrv.getJobs(5).subscribe({
      next: (response) => {
        if (response.ok && response.data?.jobs) {
          this.jobs = response.data.jobs;
          this.updateJobsPagination();
        }
        this.isLoadingJobs = false;
        this.isLoading = false;
      },
      error: () => {
        this.alertSrv.showAlert({
          alertType: AlertType.ERROR,
          mainMessage: 'Error al cargar los jobs de notificaciones',
        });
        this.isLoadingJobs = false;
        this.isLoading = false;
      },
    });
  }

  loadFunds(): void {
    this.isLoadingFunds = true;
    this.fundSrv.getFunds().subscribe({
      next: (response) => {
        if (response.ok && response.data?.demostrativeFunds) {
          this.funds = response.data.demostrativeFunds.filter((fund: IFund) => fund.active === 1);
        }
        this.isLoadingFunds = false;
      },
      error: () => {
        this.alertSrv.showAlert({
          alertType: AlertType.ERROR,
          mainMessage: 'Error al cargar los fondos',
        });
        this.isLoadingFunds = false;
      },
    });
  }

  loadActiveNotifications(): void {
    this.isLoadingNotifications = true;
    this.notificationSrv.getNotifications().subscribe({
      next: (response) => {
        if (response.ok && response.data?.notificationResponse) {
          this.activeNotifications = response.data.notificationResponse.filter(
            (notification: INotification) => notification.active === 1
          );
        }
        this.isLoadingNotifications = false;
      },
      error: () => {
        this.alertSrv.showAlert({
          alertType: AlertType.ERROR,
          mainMessage: 'Error al cargar las notificaciones',
        });
        this.isLoadingNotifications = false;
      },
    });
  }

  onFundSelected(fund: IFund): void {
    this.selectedFund = fund;
    this.showJobsTable = false;
    this.showParticipantsTable = true;
    this.loadParticipants(fund.idFund);

    const modalElement = document.getElementById('fundSelectionModal');
    if (modalElement) {
      const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  loadParticipants(fundId: string): void {
    this.isLoadingParticipants = true;
    this.notificationSendSrv.getParticipants(fundId).subscribe({
      next: (response) => {
        if (response.ok && response.data) {
          this.participants = response.data;
          this.updatePagination();
        } else {
          this.participants = [];
          this.filteredParticipants = [];
          this.paginatedParticipants = [];
        }
        this.isLoadingParticipants = false;
      },
      error: () => {
        this.alertSrv.showAlert({
          alertType: AlertType.ERROR,
          mainMessage: 'Error al cargar los participantes del fondo',
        });
        this.isLoadingParticipants = false;
      },
    });
  }

  onParticipantSelected(identification: string, isSelected: boolean): void {
    if (isSelected) {
      this.selectedParticipantsSet.add(identification);
    } else {
      this.selectedParticipantsSet.delete(identification);
    }
  }

  isParticipantSelected(identification: string): boolean {
    return this.selectedParticipantsSet.has(identification);
  }

  selectAllParticipants(): void {
    this.filteredParticipants.forEach(p => this.selectedParticipantsSet.add(p.identification));
  }

  deselectAllParticipants(): void {
    this.selectedParticipantsSet.clear();
  }

  onSearchChange(): void {
    this.searchSubject.next();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.updatePagination();
  }

  onJobsPageChange(page: number): void {
    this.currentJobsPage = page;
    this.updateJobsPagination();
  }

  updateJobsPagination(): void {
    const startIndex = (this.currentJobsPage - 1) * this.jobsItemsPerPage;
    const endIndex = startIndex + this.jobsItemsPerPage;
    this.paginatedJobs = this.jobs.slice(startIndex, endIndex);
    this.jobsTotalPages = Math.ceil(this.jobs.length / this.jobsItemsPerPage);
  }

  updatePagination(): void {
    let filtered = this.participants;

    if (this.searchTerm.trim()) {
      const term = this.searchTerm.trim().toLowerCase();
      filtered = filtered.filter(
        (participant) =>
          (participant.codUsuario ?? '').toString().includes(term) ||
          (participant.name ?? '').toLowerCase().includes(term) ||
          (participant.identification ?? '').toLowerCase().includes(term) ||
          (participant.email ?? '').toLowerCase().includes(term)
      );
    }

    this.filteredParticipants = filtered;
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedParticipants = filtered.slice(startIndex, endIndex);
    this.totalPages = Math.ceil(filtered.length / this.itemsPerPage);
  }

  openSendNotificationModal(): void {
    if (this.selectedParticipantsSet.size === 0) {
      this.alertSrv.showAlert({
        alertType: AlertType.WARNING,
        mainMessage: 'Debe seleccionar al menos un participante',
      });
      return;
    }

    if (this.receiverType !== 'EstadoCuenta' && !this.selectedNotification) {
      this.alertSrv.showAlert({
        alertType: AlertType.WARNING,
        mainMessage: 'Debe seleccionar una notificación',
      });
      return;
    }

    if (!this.subject.trim()) {
      this.alertSrv.showAlert({
        alertType: AlertType.WARNING,
        mainMessage: 'Debe ingresar un asunto',
      });
      return;
    }

    if (this.receiverType === 'EstadoCuenta') {
      this.sendEstadoCuenta();
    } else {
      this.sendNotification();
    }
  }

  sendNotification(): void {
    if (!this.selectedFund || !this.selectedNotification) return;

    const error = this.validateFiles(this.selectedFiles);
    if (error) {
      this.fileValidationError = error;
      return;
    }

    this.isSendingNotification = true;
    const fields = {
      identification: this.selectedParticipants,
      messageId: this.selectedNotification.secuencia,
      fundId: this.selectedFund.idFund,
      subject: this.subject,
      receiverType: this.receiverType,
    };

    this.notificationSendSrv.sendNotificationV2(fields, this.selectedFiles).subscribe({
      next: (response) => {
        if (response.ok && response.data) {
          this.alertSrv.showAlert({
            alertType: AlertType.SUCCESS,
            mainMessage: `Notificación enviada. Job ID: ${response.data.jobId}`,
          });

          const modalElement = document.getElementById('sendNotificationModal');
          if (modalElement) {
            const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
            if (modal) modal.hide();
          }

          this.resetForm();
          this.loadJobs();
        }
        this.isSendingNotification = false;
      },
      error: (err) => {
        const msg = err?.error?.message ?? 'Error al enviar la notificación';
        this.alertSrv.showAlert({
          alertType: AlertType.ERROR,
          mainMessage: msg,
        });
        this.isSendingNotification = false;
      },
    });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    const error = this.validateFiles(files);
    if (error) {
      this.fileValidationError = error;
      this.selectedFiles = [];
      input.value = '';
      return;
    }
    this.fileValidationError = null;
    this.selectedFiles = files;
  }

  removeFile(index: number): void {
    this.selectedFiles = this.selectedFiles.filter((_, i) => i !== index);
  }

  onReceiverTypeChange(): void {
    if (this.receiverType !== 'Email') {
      this.selectedFiles = [];
      this.fileValidationError = null;
    }
  }

  private validateFiles(files: File[]): string | null {
    if (files.length > 10) return 'Máximo 10 archivos permitidos';
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        return `El archivo "${file.name}" supera el límite de 10 MB`;
      }
    }
    return null;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  sendEstadoCuenta(): void {
    if (!this.selectedFund) return;

    this.isSendingNotification = true;
    const payload: IEstadoCuentaRequest = {
      fundId: this.selectedFund.idFund,
      month: this.month,
      year: this.year,
      identification: this.selectedParticipants,
      subject: this.subject,
    };

    this.notificationSendSrv.sendEstadoCuenta(payload).subscribe({
      next: (response) => {
        if (response.ok && response.data) {
          this.alertSrv.showAlert({
            alertType: AlertType.SUCCESS,
            mainMessage: `Estado de cuenta encolado. Job ID: ${response.data.jobId}`,
          });

          const modalElement = document.getElementById('sendNotificationModal');
          if (modalElement) {
            const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
            if (modal) {
              modal.hide();
            }
          }

          const jobId = response.data.jobId;
          this.resetForm(false);
          this.startPolling(jobId);
        }
        this.isSendingNotification = false;
      },
      error: (err) => {
        const msg = err?.error?.message ?? 'Error al enviar el estado de cuenta';
        this.alertSrv.showAlert({
          alertType: AlertType.ERROR,
          mainMessage: msg,
        });
        this.isSendingNotification = false;
      },
    });
  }

  startPolling(jobId: number): void {
    this.isPolling = true;
    this.activeJobStatus = {
      jobId,
      status: 'pending',
      totalUsers: 0,
      successCount: 0,
      failedCount: 0,
    };

    this.pollingInterval = setInterval(() => {
      this.notificationSendSrv.getJobStatus(jobId).subscribe({
        next: (response) => {
          if (response.ok && response.data) {
            this.activeJobStatus = response.data;
            if (response.data.status === 'completed' || response.data.status === 'failed') {
              this.stopPolling();
              this.loadJobs();
            }
          }
        },
        error: () => { /* ignorar errores de polling, reintentar en el siguiente tick */ },
      });
    }, 10000);
  }

  stopPolling(): void {
    if (this.pollingInterval !== null) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
  }

  dismissActiveJob(): void {
    this.activeJobStatus = null;
  }

  resetForm(clearActiveJob = true): void {
    this.selectedFund = null;
    this.selectedParticipantsSet.clear();
    this.selectedNotification = null;
    this.subject = '';
    this.receiverType = 'Email';
    this.month = new Date().getMonth() + 1;
    this.year = new Date().getFullYear();
    this.showJobsTable = true;
    this.showParticipantsTable = false;
    this.isSendingNotification = false;
    this.selectedFiles = [];
    this.fileValidationError = null;
    this.participants = [];
    this.filteredParticipants = [];
    this.paginatedParticipants = [];
    this.currentPage = 1;
    this.searchTerm = '';
    if (clearActiveJob) {
      this.activeJobStatus = null;
      this.stopPolling();
    }
  }

  goBack(): void {
    this.resetForm();
    this.loadJobs();
  }
}
